import React, { useState } from 'react';
import _ from 'lodash';

import { matchPhrases } from '../../../../utils/utilities';
import { ChecklistItem, EquipmentAnomalySummaryItem, FVIEquipmentItem, FVIPDOChecklistItem, NewAnomalyItem, PDFImageProps } from '../../../../utils/models';
import { EQUIPMENT_SUMMARY_KEY } from '../../../../utils/constants';

import AccordionItem from '../accordion-item';
import Conditional from '../../../common/conditional';
import Confirm from '../../../themed/confirm';
import EditEquipmentItemForm from '../../../forms/custom-fvi/editEquipmentItemForm';
import EquipmentOverview from '../equipment-overview';
import PIDImageForm from '../../../forms/custom-fvi/pidImageForm';
import VIGeneralDrawingForm from '../../../forms/visual-inspection/viGeneralDrawingForm';
import VIPhotoRecordForm from '../../../forms/visual-inspection/viPhotoRecordForm';

import classNames from './style.module.scss';
import useEquipmentAnomalySummaryUpdate from '../../../../hooks/useEquipmentAnomalySummaryUpdate';

interface ExistingEquipmentItemProps {
	equipmentItem: FVIEquipmentItem;
	nextAnomalyID: number;
	showLastLineOption: boolean;
	isNewFormat: boolean;
	onChangeAdditionalComment: (equipmentNumber: string, comment: string) => void;
	onChangeChecklistItems: (equipmentNumber: string, items: ChecklistItem[]) => void;
	onChangePID: (equipmentnumber: string, pIDData: Pick<FVIEquipmentItem, 'pIDImages' | 'pIDNumber'>) => void;
	onEditEquipmentItem: (initialEquipmentItem: FVIEquipmentItem, updatedEquipmentItem: Pick<FVIEquipmentItem, 'equipmentNumber' | 'hasPID' | 'hasAnomalies' | 'inspectionType' | 'inspectionDescription'>) => void;
	onDeleteEquipmentItem: (equipmentNumber: string) => void;
	onChangeEquipmentAnomaly: (equipmentNumber: string, items: NewAnomalyItem[]) => void;
	onChangeGeneralPhotos: (equipmentNumber: string, images: PDFImageProps[]) => void;
	onChangeGeneralDrawing: (equipmentNumber: string, images: PDFImageProps[]) => void;
	onChangePDOChecklist: (equipmentNumber: string, items: FVIPDOChecklistItem[]) => void;
	onSaveAnomalySummary: (equipmentNumber: string, anomalySummary: EquipmentAnomalySummaryItem) => void;
}

const ExistingEquipmentItem: React.FC<ExistingEquipmentItemProps> = ({
	equipmentItem,
	nextAnomalyID,
	showLastLineOption,
	isNewFormat,
	onChangePID,
	onDeleteEquipmentItem,
	onEditEquipmentItem,
	onChangeChecklistItems,
	onChangeEquipmentAnomaly,
	onChangeGeneralPhotos,
	onChangeGeneralDrawing,
	onChangeAdditionalComment,
	onChangePDOChecklist,
	onSaveAnomalySummary,
}) => {
	const [editEquipmentItem, setEditEquipmentItem] = useState(false);
	const [deleteEquipmentItem, setDeleteEquipmentItem] = useState(false);
	const [isOpen, setOpen] = useState(false);

	const handleEditPipingItem = (newEquipmentDetails: Pick<FVIEquipmentItem, 'equipmentNumber' | 'equipmentDescription' | 'hasPID' | 'hasAnomalies' | 'inspectionType' | 'inspectionDescription'>) => {
		onEditEquipmentItem(equipmentItem, newEquipmentDetails);
		setEditEquipmentItem(false);
		setOpen(true);
	};

	const handleToggleEdit = () => {
		setEditEquipmentItem((previous) => !previous);
	};

	const handleConfirmDeletion = () => {
		onDeleteEquipmentItem(equipmentItem.equipmentNumber);
	};

	const handleToggleDeletion = () => {
		setDeleteEquipmentItem((previous) => !previous);
	};

	useEquipmentAnomalySummaryUpdate({
		isNewFormat,
		anomalyItems: equipmentItem.anomalyItems, 
		anomalySummary: equipmentItem.anomalySummary,
		onSaveAnomalySummary: (summary) => onSaveAnomalySummary(equipmentItem.equipmentNumber, summary),
	});

	return (
		<>
			<Conditional isVisible={deleteEquipmentItem}>
				<Confirm
					title={'Delete Equipment Tag ' + equipmentItem.equipmentNumber + '?'}
					message="Are you sure you want to delete this item? This action is not reversible!"
					onCancel={handleToggleDeletion}
					onConfirm={handleConfirmDeletion}
				/>
			</Conditional>

			<Conditional isVisible={editEquipmentItem}>
				<div className="bg-white round border p-3">
					<EditEquipmentItemForm
						equipmentDetails={_.pick(equipmentItem, ['equipmentNumber', 'equipmentDescription', 'hasPDO', 'hasPID', 'hasAnomalies', 'inspectionType', 'inspectionDescription'])} 
						onEdit={handleEditPipingItem}
						onCancelEdit={handleToggleEdit}
						showLastLineOption={showLastLineOption}
					/>
				</div>
			</Conditional>

			<Conditional isVisible={!deleteEquipmentItem && !editEquipmentItem}>
				<div className={classNames.accordionItem}>
					<div
						className={classNames.accordionItemHeader}
						onClick={() => setOpen((prev) => !prev)}
						role="button"
					>
						<div>Equipment Tag: {equipmentItem.equipmentNumber}</div>

						<div className="d-flex justify-content-center align-items-center">
							<button
								className={classNames.accordionItemEdit}
								onClick={handleToggleEdit}
							>
								<i className="bi bi-pencil"></i>
							</button>

							<button
								className={classNames.accordionItemDelete}
								onClick={handleToggleDeletion}
							>
								<i className="bi bi-trash"></i>
							</button>

							<div className={classNames.accordionItemIcon}>
								<i className={`bi bi-chevron-${isOpen ? 'down' : 'right'}`}></i>
							</div>
						</div>
					</div>

					<Conditional isVisible={isOpen}>
						<div className={classNames.accordionItemBody}>
							<Conditional isVisible={matchPhrases(equipmentItem.hasPID, 'yes')}>
								<AccordionItem isActive title="P&ID">
									<PIDImageForm
										initialPIDData={_.pick(equipmentItem, ['pIDNumber', 'pIDImages'])}
										onAdd={(pIDData: Pick<FVIEquipmentItem, 'pIDImages' | 'pIDNumber'>) => {
											onChangePID(equipmentItem.equipmentNumber, pIDData);
										}}
									/>
								</AccordionItem>
							</Conditional>

							<Conditional isVisible={true}>
								<AccordionItem isActive title="General Drawing (Optional)">
									<VIGeneralDrawingForm
										initialGeneralDrawing={equipmentItem.generalDrawing ?? []}
										onProceed={(images: PDFImageProps[]) => onChangeGeneralDrawing(equipmentItem.equipmentNumber, images)}
									/>
								</AccordionItem>
							</Conditional>

							<EquipmentOverview
								equipmentId={equipmentItem.id}
								anomalyItems={equipmentItem.anomalyItems}
								equipmentNumber={equipmentItem.equipmentNumber}
								additionalComment={equipmentItem.additionalComment}
								anomalySummary={equipmentItem.anomalySummary}
								checklistItems={equipmentItem.checklistItems}
								hasAnomalies={equipmentItem.hasAnomalies}
								isPDOFormat={matchPhrases(equipmentItem.hasPDO, 'yes')}
								pdoChecklistItems={equipmentItem.pdoChecklist}
								onChangePDOChecklistItems={(items) => onChangePDOChecklist(equipmentItem.equipmentNumber, items)}
								nextAnomalyID={nextAnomalyID}
								isNewFormat={isNewFormat}
								isChecklistVisible={matchPhrases(equipmentItem.inspectionType, 'external')}
								onChangeAnomalies={(items: NewAnomalyItem[]) => onChangeEquipmentAnomaly(equipmentItem.equipmentNumber, items)}
								onChangeSummary={(summary: EquipmentAnomalySummaryItem) => onSaveAnomalySummary(equipmentItem.equipmentNumber, summary)}
								onChangeAdditionalComment={(comment) => onChangeAdditionalComment(equipmentItem.equipmentNumber, comment)}
								onChangeChecklistItems={(items) => onChangeChecklistItems(equipmentItem.equipmentNumber, items)}
							/>

							<Conditional isVisible={true}>
								<AccordionItem isActive title="General Photos (Optional)">
									<VIPhotoRecordForm
										initialGeneralPhotos={equipmentItem.generalPhotos}
										onProceed={(images: PDFImageProps[]) => onChangeGeneralPhotos(equipmentItem.equipmentNumber, images)}
									/>
								</AccordionItem>
							</Conditional>
						</div>
					</Conditional>
				</div>
			</Conditional>
		</>
	);
};

export const EQUIPMENT_LOCATION_OPTIONS = [
	{
		label: EQUIPMENT_SUMMARY_KEY.BODY,
		value: EQUIPMENT_SUMMARY_KEY.BODY
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.FLANGE,
		value: EQUIPMENT_SUMMARY_KEY.FLANGE,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.FASTENERS,
		value: EQUIPMENT_SUMMARY_KEY.FASTENERS,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.VALVE,
		value: EQUIPMENT_SUMMARY_KEY.VALVE,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.UNDER_SUPPORT,
		value: EQUIPMENT_SUMMARY_KEY.UNDER_SUPPORT,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.SUPPORT,
		value: EQUIPMENT_SUMMARY_KEY.SUPPORT,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.PIPE,
		value: EQUIPMENT_SUMMARY_KEY.PIPE,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.GUAGE,
		value: EQUIPMENT_SUMMARY_KEY.GUAGE,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.DISH,
		value: EQUIPMENT_SUMMARY_KEY.DISH,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.TOP_DISH,
		value: EQUIPMENT_SUMMARY_KEY.TOP_DISH,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.BOTTOM_DISH,
		value: EQUIPMENT_SUMMARY_KEY.BOTTOM_DISH,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.NOZZLE,
		value: EQUIPMENT_SUMMARY_KEY.NOZZLE,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.NAME_PLATE,
		value: EQUIPMENT_SUMMARY_KEY.NAME_PLATE,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.SKIRT,
		value: EQUIPMENT_SUMMARY_KEY.SKIRT,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.STRUCTURE,
		value: EQUIPMENT_SUMMARY_KEY.STRUCTURE,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.EXTERNAL_PROTECTION,
		value: EQUIPMENT_SUMMARY_KEY.EXTERNAL_PROTECTION,
	},
	{
		label: EQUIPMENT_SUMMARY_KEY.INSULATION,
		value: EQUIPMENT_SUMMARY_KEY.INSULATION,
	},
];

export default ExistingEquipmentItem;
